/**
 * Wizard Stepper Component
 *
 * Shows progress through the 5-step evaluation wizard.
 */

interface WizardStep {
  number: number;
  label: string;
  shortLabel: string;
}

const WIZARD_STEPS_V1: WizardStep[] = [
  { number: 1, label: 'Context & Voorbereiding', shortLabel: 'Context' },
  { number: 2, label: 'Taken Beoordelen', shortLabel: 'Taken' },
  { number: 3, label: 'Vakcompetenties', shortLabel: 'Vakcomp.' },
  { number: 4, label: 'Basiscompetenties', shortLabel: 'Basiscomp.' },
  { number: 5, label: 'Review & Totaalscore', shortLabel: 'Review' },
  { number: 6, label: 'Afspraken & Afronden', shortLabel: 'Afronden' },
];

// Epic 4 (Story 4.2, AC #2, AD-4): "Taken" -> "Kerntaken" voor
// core_tasks_v2-beoordelingen. Alleen stap 2's label wijzigt.
const WIZARD_STEPS_V2: WizardStep[] = WIZARD_STEPS_V1.map((step) =>
  step.number === 2 ? { ...step, label: 'Kerntaken Beoordelen', shortLabel: 'Kerntaken' } : step
);

interface WizardStepperProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
  canNavigateToStep?: (step: number) => boolean;
  isStepComplete?: (step: number) => boolean;
  /** Epic 4 (Story 4.2, AD-4) — bepaalt of stap 2 "Taken" of "Kerntaken" heet. */
  scoringModel?: 'tasks_v1' | 'core_tasks_v2';
}

export function WizardStepper({
  currentStep,
  onStepClick,
  canNavigateToStep,
  isStepComplete,
  scoringModel,
}: WizardStepperProps) {
  const WIZARD_STEPS = scoringModel === 'core_tasks_v2' ? WIZARD_STEPS_V2 : WIZARD_STEPS_V1;
  return (
    <nav aria-label="Progress" className="mb-8">
      <ol className="flex items-center justify-between">
        {WIZARD_STEPS.map((step, index) => {
          const isActive = currentStep === step.number;
          const isComplete = isStepComplete?.(step.number) ?? false;
          const canNavigate = canNavigateToStep?.(step.number) ?? false;
          const isLast = index === WIZARD_STEPS.length - 1;

          return (
            <li key={step.number} className="relative flex-1">
              <div className="flex items-center">
                {/* Step circle */}
                <button
                  onClick={() => canNavigate && onStepClick?.(step.number)}
                  disabled={!canNavigate}
                  className={`
                    relative flex h-10 w-10 items-center justify-center rounded-full
                    transition-colors duration-200
                    ${isActive
                      ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                      : isComplete
                        ? 'bg-green-600 text-white cursor-pointer hover:bg-green-700'
                        : canNavigate
                          ? 'bg-gray-200 text-gray-600 cursor-pointer hover:bg-gray-300'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }
                  `}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {isComplete && !isActive ? (
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span className="text-sm font-medium">{step.number}</span>
                  )}
                </button>

                {/* Connector line */}
                {!isLast && (
                  <div className="flex-1 h-0.5 mx-2">
                    <div
                      className={`h-full transition-colors duration-200 ${
                        isComplete ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                )}
              </div>

              {/* Step label */}
              <div className="absolute left-0 right-0 mt-2 text-center">
                <span
                  className={`text-xs font-medium ${
                    isActive
                      ? 'text-blue-600'
                      : isComplete
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`}
                >
                  <span className="hidden sm:inline">{step.label}</span>
                  <span className="sm:hidden">{step.shortLabel}</span>
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
